import { includes } from 'lodash';

import { getNumberValue } from '../service/FormatHelpers/FormatHelpers';
import { getRecommendationCharacterByType } from '../service/RecommendationsHelpers/RecommendationsHelpers';
import { BannerModerationTheme, GroupType } from '../typings/domain';
import {
    GdAdGroupType,
    GdAdType,
    GdiRecommendationType,
    recommendationFragment,
    recommendationKeyFragment,
    recommendationsGridSummaryRowFragment,
    summaryRecommendationFragment,
    summaryRecommendationGraphFragment
} from '../typings/graphql-grid';
import {
    ContextRecommendation,
    Recommendation,
    RecommendationChartData,
    RecommendationChartDataAxisXType,
    RecommendationChartDataAxisYType,
    RecommendationKey,
    RecommendationType,
    SummaryRecommendation
} from '../typings/recommendations';

import { getValueFromApiFormat } from './common/common';

export function mapBaseRecommendationInfoFromApiFormat(
    apiRecommendation:
        | recommendationFragment
        | summaryRecommendationFragment
        | recommendationsGridSummaryRowFragment
): Recommendation {
    const type = mapRecommendationTypeFromApiFormat(apiRecommendation.type);
    const keys = apiRecommendation.keys.map(mapRecommendationKeyFromApiFormat);
    const status = apiRecommendation.status;
    const isApplicable = apiRecommendation.isApplicable;

    if (type === RecommendationType.sendLicenses) {
        return {
            type,
            character: getRecommendationCharacterByType(type),
            status,
            keys,
            isApplicable,
            theme: getBannerModerationThemeByRecommendationApiType(
                apiRecommendation.type
            )
        };
    }

    if (
        (type === RecommendationType.dailyBudget ||
            type === RecommendationType.removePagesFromBlackList ||
            type === RecommendationType.switchOnAutotargeting) &&
        (apiRecommendation.__typename === 'GdRecommendationWithKpi' ||
            apiRecommendation.__typename === 'GdRecommendationSummaryWithKpi')
    ) {
        const kpi = apiRecommendation.kpi;

        return {
            type,
            character: getRecommendationCharacterByType(type),
            status,
            isApplicable,
            keys,
            kpi: {
                sign: getValueFromApiFormat(kpi.sign),
                periodValue: kpi.period,
                clicks: Number(kpi.clicks),
                cost: Number(kpi.cost),
                currency: kpi.currency,
                graph: mapRecommendationGraphDataFromApiFormat(kpi.graph),
                recommendedDailyBudget:
                    kpi.__typename === 'GdRecommendationKpiDailyBudget'
                        ? Number(kpi.recommendedDailyBudget)
                        : 0
            }
        };
    }
    // запрашиваем все параметры kpi, так как на беке пока нет возможности создавать рекомендацию без kpi полей
    // https://st.yandex-team.ru/DIRECT-101841
    if (
        type === RecommendationType.addBannerFormats &&
        apiRecommendation.__typename === 'GdRecommendationWithKpi' &&
        apiRecommendation.kpi.__typename ===
            'GdRecommendationKpiAddBannerFormats'
    ) {
        const kpi = apiRecommendation.kpi;

        return {
            type,
            character: getRecommendationCharacterByType(type),
            status,
            keys,
            isApplicable,
            kpi: {
                formats: kpi.formats,
                periodValue: kpi.period,
                clicks: Number(kpi.clicks),
                cost: Number(kpi.cost),
                currency: kpi.currency,
                recommendedDailyBudget: 0
            }
        };
    }

    if (
        (type === RecommendationType.chooseAppropriatePlacementsForAdGroup ||
            type === RecommendationType.uploadAppropriateCreatives ||
            type === RecommendationType.chooseAppropriatePlacementsForBanner) &&
        apiRecommendation.__typename === 'GdRecommendationWithKpi' &&
        (apiRecommendation.kpi.__typename ===
            'GdRecommendationKpiChooseAppropriatePlacementsForAdGroup' ||
            apiRecommendation.kpi.__typename ===
                'GdRecommendationKpiUploadAppropriateCreatives' ||
            apiRecommendation.kpi.__typename ===
                'GdRecommendationKpiChooseAppropriatePlacementsForBanner')
    ) {
        const kpi = apiRecommendation.kpi;

        return {
            type,
            character: getRecommendationCharacterByType(type),
            status,
            keys,
            isApplicable,
            kpi: {
                videoFormats: getValueFromApiFormat(kpi.videoFormats),
                periodValue: kpi.period,
                clicks: Number(kpi.clicks),
                cost: Number(kpi.cost),
                currency: kpi.currency,
                recommendedDailyBudget: 0
            }
        };
    }

    if (
        type === RecommendationType.changeAdGroupWithLowStat ||
        type === RecommendationType.changeAdGroupForModeration ||
        type === RecommendationType.changeBannerForModeration ||
        type === RecommendationType.addBannerDisplayHrefs ||
        type === RecommendationType.addTitleExtension ||
        type === RecommendationType.addSiteLinks ||
        type === RecommendationType.addAdditionsItemCallouts ||
        type === RecommendationType.addImageToBanner
    ) {
        return {
            type,
            keys,
            isApplicable,
            character: getRecommendationCharacterByType(type),
            status
        };
    }

    throw new Error(`Неподдерживаемый тип рекомендации: ${type}`);
}

export function mapRecommendationGraphDataFromApiFormat(
    apiData: summaryRecommendationGraphFragment
): RecommendationChartData | undefined {
    const { typeX, typeY, points } = apiData;

    return {
        profitValues: points.map(point => Number(point.profitValue)),
        currentValues: points.map(point => Number(point.currentValue)),
        xAxis: {
            type: RecommendationChartDataAxisXTypeFromApiFormat(typeX),
            values: points.map(point => ({
                point: point.valueX,
                holiday: point.holiday === '1' ? true : false
            }))
        },
        yAxis: {
            type: RecommendationChartDataAxisYTypeFromApiFormat(typeY)
        }
    };
}

function RecommendationChartDataAxisXTypeFromApiFormat(
    apiType: string
): RecommendationChartDataAxisXType {
    const dictionary: Record<string, RecommendationChartDataAxisXType> = {
        date: RecommendationChartDataAxisXType.date
    };

    const type = dictionary[apiType];

    if (!type) {
        throw Error(
            `Неподдерживаемый тип оси X графика рекомендации ${apiType}`
        );
    }

    return type;
}

function RecommendationChartDataAxisYTypeFromApiFormat(
    apiType: string
): RecommendationChartDataAxisYType {
    const dictionary: Record<string, RecommendationChartDataAxisYType> = {
        clicks: RecommendationChartDataAxisYType.clicks,
        ctr: RecommendationChartDataAxisYType.ctr
    };

    const type = dictionary[apiType];

    if (!type) {
        throw Error(
            `Неподдерживаемый тип оси Y графика рекомендации ${apiType}`
        );
    }

    return type;
}

export function mapContextRecommendationFromApiFormat(
    apiRecommendation: recommendationFragment
): ContextRecommendation {
    const result = {
        ...mapBaseRecommendationInfoFromApiFormat(apiRecommendation),
        keys: apiRecommendation.keys.map(mapRecommendationKeyFromApiFormat)
    };

    if (
        result.type === RecommendationType.removePagesFromBlackList &&
        apiRecommendation.__typename === 'GdRecommendationWithKpi' &&
        apiRecommendation.kpi.__typename ===
            'GdRecommendationKpiRemovePagesFromBlackListOfACampaign'
    ) {
        return {
            ...result,
            items: apiRecommendation.kpi.pageNames
        };
    }

    if (
        result.type === RecommendationType.dailyBudget &&
        apiRecommendation.__typename === 'GdRecommendationWithKpi' &&
        apiRecommendation.kpi.__typename === 'GdRecommendationKpiDailyBudget'
    ) {
        return {
            ...result,
            recommendedDailyBudget: getNumberValue(
                apiRecommendation.kpi.recommendedDailyBudget
            )
        };
    }

    return result;
}

export function mapSummaryRecommendationFromApiFormat(
    apiRecommendation: summaryRecommendationFragment
): SummaryRecommendation {
    const baseRecommendationInfo = mapBaseRecommendationInfoFromApiFormat(
        apiRecommendation
    );

    if (
        apiRecommendation.__typename === 'GdRecommendationSummaryWithKpi' &&
        (baseRecommendationInfo.type === RecommendationType.dailyBudget ||
            baseRecommendationInfo.type ===
                RecommendationType.removePagesFromBlackList ||
            baseRecommendationInfo.type ===
                RecommendationType.switchOnAutotargeting)
    ) {
        return {
            ...baseRecommendationInfo,
            items: {
                data: apiRecommendation.items.rowset.map(
                    apiRecommendationItem => {
                        return {
                            name: apiRecommendationItem.name,
                            clicks: Number(apiRecommendationItem.clicks),
                            cost: Number(apiRecommendationItem.cost)
                        };
                    }
                ),
                campaignsIds: apiRecommendation.campaignIds,
                totalCount: apiRecommendation.totalCount
            }
        };
    }

    if (
        baseRecommendationInfo.type ===
            RecommendationType.changeAdGroupWithLowStat ||
        baseRecommendationInfo.type ===
            RecommendationType.changeAdGroupForModeration ||
        baseRecommendationInfo.type ===
            RecommendationType.changeBannerForModeration ||
        baseRecommendationInfo.type ===
            RecommendationType.addBannerDisplayHrefs ||
        baseRecommendationInfo.type === RecommendationType.addTitleExtension ||
        baseRecommendationInfo.type === RecommendationType.addSiteLinks ||
        baseRecommendationInfo.type ===
            RecommendationType.addAdditionsItemCallouts ||
        baseRecommendationInfo.type === RecommendationType.addImageToBanner
    ) {
        return {
            ...baseRecommendationInfo,
            items: {
                campaignsIds: apiRecommendation.campaignIds,
                totalCount: apiRecommendation.totalCount
            }
        };
    }

    if (baseRecommendationInfo.type === RecommendationType.sendLicenses) {
        return {
            ...baseRecommendationInfo,
            items: {
                campaignsIds: apiRecommendation.campaignIds,
                totalCount: apiRecommendation.totalCount
            }
        };
    }

    throw Error('Неподдерживаемый тип рекомендации');
}

export function mapRecommendationKeyFromApiFormat(
    key: recommendationKeyFragment
): RecommendationKey {
    return {
        userKey1: key.userKey1,
        userKey2: key.userKey2,
        userKey3: key.userKey3,
        pid: key.pid,
        bid: key.bid,
        cid: key.cid,
        timestamp: key.timestamp,
        type: key.type
    };
}

export function isRecommendationSupported(
    type: GdiRecommendationType
): boolean {
    const supportedRecommendationTypes = [
        GdiRecommendationType.addMorePicturesToAdgroup,
        GdiRecommendationType.changeAdGroupForModeration,
        GdiRecommendationType.changeAdGroupWithLowStat,
        GdiRecommendationType.dailyBudget,
        GdiRecommendationType.removePagesFromBlackList,
        GdiRecommendationType.removePagesFromBlackListOfACampaign,
        GdiRecommendationType.switchOnAutotargeting,
        GdiRecommendationType.sendLicensesBanks,
        GdiRecommendationType.sendLicensesMedServices,
        GdiRecommendationType.sendLicensesPharmacy,
        GdiRecommendationType.changeBannerForModeration,
        GdiRecommendationType.addBannerDisplayHrefs,
        GdiRecommendationType.addTitleExtension,
        GdiRecommendationType.addSiteLinks,
        GdiRecommendationType.addAdditionItemCallouts,
        GdiRecommendationType.addImageToBanner
    ];

    return includes(supportedRecommendationTypes, type);
}

export function isRecommendationSupportedOnGridByGroupType(
    recommendationType: GdiRecommendationType,
    groupType: GdAdGroupType
): boolean {
    const dictionary = {
        [GdAdGroupType.CPM_BANNER]: [GdiRecommendationType.addBannerFormats],
        [GdAdGroupType.CPM_OUTDOOR]: [
            GdiRecommendationType.chooseAppropriatePlacementsForAdGroup,
            GdiRecommendationType.uploadAppropriateCreatives
        ]
    };

    if (!dictionary[groupType]) {
        return false;
    }

    return dictionary[groupType].includes(recommendationType);
}

export function isRecommendationSupportedOnGridByAdsType(
    recommendationType: GdiRecommendationType,
    bannerType: GdAdType
): boolean {
    const dictionary = {
        [GdAdGroupType.CPM_OUTDOOR]: [
            GdiRecommendationType.chooseAppropriatePlacementsForBanner
        ]
    };

    if (!dictionary[bannerType]) {
        return false;
    }

    return dictionary[bannerType].includes(recommendationType);
}

export function mapRecommendationTypeFromApiFormat(
    type: GdiRecommendationType
): RecommendationType {
    const dictionary = {
        [GdiRecommendationType.addMorePicturesToAdgroup]:
            RecommendationType.addMorePicturesToAdgroup,
        [GdiRecommendationType.changeAdGroupForModeration]:
            RecommendationType.changeAdGroupForModeration,
        [GdiRecommendationType.changeBannerForModeration]:
            RecommendationType.changeBannerForModeration,
        [GdiRecommendationType.addBannerDisplayHrefs]:
            RecommendationType.addBannerDisplayHrefs,
        [GdiRecommendationType.addTitleExtension]:
            RecommendationType.addTitleExtension,
        [GdiRecommendationType.addSiteLinks]: RecommendationType.addSiteLinks,
        [GdiRecommendationType.addAdditionItemCallouts]:
            RecommendationType.addAdditionsItemCallouts,
        [GdiRecommendationType.addImageToBanner]:
            RecommendationType.addImageToBanner,
        [GdiRecommendationType.changeAdGroupWithLowStat]:
            RecommendationType.changeAdGroupWithLowStat,
        [GdiRecommendationType.dailyBudget]: RecommendationType.dailyBudget,
        [GdiRecommendationType.removePagesFromBlackList]:
            RecommendationType.removePagesFromBlackList,
        [GdiRecommendationType.removePagesFromBlackListOfACampaign]:
            RecommendationType.removePagesFromBlackList,
        [GdiRecommendationType.switchOnAutotargeting]:
            RecommendationType.switchOnAutotargeting,

        [GdiRecommendationType.sendLicensesBanks]:
            RecommendationType.sendLicenses,
        [GdiRecommendationType.sendLicensesMedServices]:
            RecommendationType.sendLicenses,
        [GdiRecommendationType.sendLicensesPharmacy]:
            RecommendationType.sendLicenses,
        [GdiRecommendationType.addBannerFormats]:
            RecommendationType.addBannerFormats,
        [GdiRecommendationType.chooseAppropriatePlacementsForAdGroup]:
            RecommendationType.chooseAppropriatePlacementsForAdGroup,
        [GdiRecommendationType.uploadAppropriateCreatives]:
            RecommendationType.uploadAppropriateCreatives,
        [GdiRecommendationType.chooseAppropriatePlacementsForBanner]:
            RecommendationType.chooseAppropriatePlacementsForBanner
    };

    if (!dictionary[type]) {
        throw new Error(
            `Для статуса рекомендации в api формате ${type} не определено значение`
        );
    }

    return dictionary[type];
}

function getBannerModerationThemeByRecommendationApiType(
    apiRecommendationType: GdiRecommendationType
): BannerModerationTheme {
    const dictionary = {
        [GdiRecommendationType.sendLicensesBanks]: BannerModerationTheme.banks,
        [GdiRecommendationType.sendLicensesMedServices]:
            BannerModerationTheme.medServices,
        [GdiRecommendationType.sendLicensesPharmacy]:
            BannerModerationTheme.pharmacy
    };

    if (!dictionary[apiRecommendationType]) {
        throw new Error(
            `Для типа рекомендации ${apiRecommendationType} не определено значение запрещенной тематики`
        );
    }

    return dictionary[apiRecommendationType];
}

export function isRecommendationSupportedOnEditBannerPage(
    recommendationType: GdiRecommendationType,
    groupType: GdAdGroupType
): boolean {
    const dictionary = {
        [GdAdGroupType.CPM_BANNER]: [GdiRecommendationType.addBannerFormats],
        [GdAdGroupType.CPM_OUTDOOR]: [
            GdiRecommendationType.uploadAppropriateCreatives
        ]
    };

    if (!dictionary[groupType]) {
        return false;
    }

    return dictionary[groupType].includes(recommendationType);
}

export function isRecommendationSupportedOnEditGroupPage(
    recommendationType: GdiRecommendationType,
    groupType: GdAdGroupType
): boolean {
    const dictionary = {
        [GdAdGroupType.CPM_OUTDOOR]: [
            GdiRecommendationType.chooseAppropriatePlacementsForAdGroup
        ]
    };

    if (!dictionary[groupType]) {
        return false;
    }

    return dictionary[groupType].includes(recommendationType);
}

export function isGroupSupportsRecommendationsOnEditBannerPage(
    type: GroupType
): boolean {
    switch (type) {
        case GroupType.CPM_BANNER:
        case GroupType.CPM_OUTDOOR:
            return true;
        default:
            return false;
    }
}
